/**
 * Upload Files Action
 * Opens the global upload dialog to upload files to a specific folder
 */

import { Datum } from "../datastructure";
import { ActionContext, getFilePath } from "../actions/types";
import useFileTrayStore, {
  type FileWithStatus,
} from "@/components/common/FileTree/UploadStuff/useFileTrayStore";
import uploadFilesService from "@/components/common/FileTree/UploadStuff/services/uploadFiles";
import { queryClient } from "@/ReactQuery/reactQueryClient";
import { v4 as uuidv4 } from "uuid";

export interface UploadFilesOptions {
  /** Target folder path segments (empty for root) */
  prependFilePath: string[];
  /** Organization ID */
  orgId?: string;
  /** Drive ID */
  driveId?: string;
  /** Whether to show drive selection in the upload dialog */
  driveSelection?: boolean;
}

/**
 * Opens the global upload dialog for uploading files
 */
export function openUploadDialog(options: UploadFilesOptions): void {
  const store = useFileTrayStore.getState();
  store.openGlobalUploadDialog({
    prependFilePath: options.prependFilePath,
    metaData: {
      orgId: options.orgId,
      workspaceId: "",
      useFilesystem: true,
      driveId: options.driveId,
    },
    driveSelection: options.driveSelection,
  });
}

/**
 * Opens upload dialog targeting a specific folder
 */
export async function uploadFilesToFolder(
  datum: Datum,
  context: ActionContext
): Promise<void> {
  if (datum.type !== "folder") {
    console.error("uploadFilesToFolder: Target must be a folder");
    return;
  }

  const folderPath = getFilePath(datum);
  const driveId = datum.payload?.driveId || context.driveId;

  openUploadDialog({
    prependFilePath: folderPath,
    orgId: context.orgId,
    driveId,
    driveSelection: false,
  });
}

export interface DirectUploadOptions {
  /** Target folder path segments (empty for root). */
  prependFilePath: string[];
  orgId?: string;
  driveId?: string;
}

/**
 * Upload files the browser's own dialog chose, through the tray — the same
 * entries, progress and duplicate handling the upload dialog gives — without
 * the dialog (#6559 round 12: "Upload files" and "Upload folder" go straight
 * to the browser's dialog). Invalidates the tree and the Documents listing
 * when the batch is done, so what was uploaded is listed where it landed.
 */
export async function uploadFilesThroughTray(
  files: File[],
  options: DirectUploadOptions
): Promise<void> {
  const store = useFileTrayStore.getState();
  const trayFiles = files.map((file) => ({
    ...file,
    name: file.name,
    tempId: uuidv4(),
    status: "pending" as const,
    originalFile: file,
  })) as FileWithStatus[];
  for (const file of trayFiles) store.addFile(file);
  await uploadFilesService(
    files,
    options.prependFilePath,
    {
      orgId: options.orgId,
      workspaceId: "",
      useFilesystem: true,
      driveId: options.driveId,
    },
    trayFiles
  );
  // The service updates each tray entry itself as it completes; the tree
  // is invalidated here, and the Documents listing follows the tray (its
  // page watches entries settle), so the listing is not touched twice.
  await queryClient.invalidateQueries({ queryKey: ["orgFileList"] });
}
